/**
 * Confirm moving the Account's tier from the plan page (RP-12.8).
 *
 * Says exactly what will happen before anything is written — the preview comes from the
 * same server function the write uses, so the dialog and the outcome cannot disagree:
 *
 *   • the Account moves to the tier (every new plan on it uses the new Rate Basis);
 *   • THIS plan is re-priced only if it is a Draft, with revenue before → after
 *     (and margin, for a cost reader);
 *   • custom-rate lines keep their rate; other Draft plans on the Account keep theirs.
 *
 * Product decisions 2026-10-17: the lever moves the Account's tier; re-pricing is Draft only.
 */

import { AlertTriangle } from "lucide-react";
import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Skeleton } from "@/components/ui/skeleton";
import {
  type PlanRateCardView,
  type PlanTierPreviewView,
  useChangePlanTier,
  usePlanTierPreview,
} from "@/hooks/use-plan-rate-card";

function dollars(value: string): string {
  const n = Number(value);
  return Number.isFinite(n)
    ? n.toLocaleString(undefined, { style: "currency", currency: "USD" })
    : `$${value}`;
}

export function PlanTierChangeDialog({
  card,
  tierId,
  onClose,
}: {
  card: PlanRateCardView;
  tierId: string;
  onClose: () => void;
}) {
  const [alsoSetSolutionTier, setAlsoSetSolutionTier] = useState(false);
  const preview = usePlanTierPreview(card.planId, tierId, alsoSetSolutionTier);
  const change = useChangePlanTier(card.planId);
  const tierName = card.tiers.find((t) => t.id === tierId)?.name ?? "the new tier";

  return (
    <Dialog
      open
      onOpenChange={(open) => {
        if (!open && !change.isPending) onClose();
      }}
    >
      <DialogContent className="max-w-[560px]">
        <DialogHeader>
          <DialogTitle>
            Move {card.clientAccountName} to the {tierName} Engagement Standard Tier?
          </DialogTitle>
          <DialogDescription>
            This changes {card.clientAccountName}'s Engagement Standard Tier everywhere — every new
            plan on the Account prices on its Rate Basis. Pod Size on existing Solutions is never
            changed.
          </DialogDescription>
        </DialogHeader>

        {preview.isLoading && <Skeleton className="h-24 w-full" />}
        {preview.isError && (
          <p className="text-sm text-destructive">
            {preview.error instanceof Error ? preview.error.message : "Could not load the preview."}
          </p>
        )}
        {preview.data && (
          <PreviewBody
            preview={preview.data}
            card={card}
            alsoSetSolutionTier={alsoSetSolutionTier}
            onAlsoSetSolutionTier={setAlsoSetSolutionTier}
          />
        )}

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={change.isPending}>
            Cancel
          </Button>
          <Button
            disabled={!preview.data || change.isPending}
            onClick={() =>
              change.mutate(
                { tierId, alsoSetSolutionTier },
                {
                  onSuccess: onClose,
                },
              )
            }
          >
            {change.isPending ? "Saving…" : `Move to ${tierName}`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function PreviewBody({
  preview,
  card,
  alsoSetSolutionTier,
  onAlsoSetSolutionTier,
}: {
  preview: PlanTierPreviewView;
  card: PlanRateCardView;
  alsoSetSolutionTier: boolean;
  onAlsoSetSolutionTier: (value: boolean) => void;
}) {
  const reprices = preview.blocked === null && !preview.sameBasis;
  return (
    <div className="space-y-3 text-sm">
      <p>
        <span className="font-medium">{preview.tierName}</span> prices on{" "}
        <span className="font-medium">{preview.nextRateBasisName}</span> rates.
      </p>

      {preview.solutionOwnTier !== null && (
        <div className="flex items-start gap-2 rounded-md border border-border p-3">
          <Checkbox
            id="plan-tier-also-solution"
            checked={alsoSetSolutionTier}
            onCheckedChange={(v) => onAlsoSetSolutionTier(v === true)}
            className="mt-0.5"
          />
          <label htmlFor="plan-tier-also-solution">
            Also move this plan's Solution to {preview.tierName}. It has its own tier (
            {preview.solutionOwnTier.name}), which outranks the Account's.
          </label>
        </div>
      )}

      <div className="rounded-md bg-muted/40 p-3">
        <p className="mb-1 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
          This plan
        </p>
        {preview.blocked !== null ? (
          <p className="flex items-start gap-2">
            {preview.blocked.kind === "incompleteBasis" ||
            preview.blocked.kind === "missingRates" ? (
              <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-warning-700" />
            ) : null}
            <span>
              {preview.blocked.message}
              {preview.blocked.kind === "incompleteBasis" &&
                ` Until those rates exist, no new plan can be created on ${card.clientAccountName} either.`}
            </span>
          </p>
        ) : preview.sameBasis ? (
          <p>Already prices on {card.rateBasisLabel}, so its rates do not change.</p>
        ) : (
          <div className="space-y-1">
            <p>
              Re-prices {preview.repricedLineCount}{" "}
              {preview.repricedLineCount === 1 ? "Plan Line" : "Plan Lines"} from{" "}
              {card.rateBasisLabel} to {preview.nextRateBasisLabel}.
              {preview.keptLineCount > 0 &&
                ` ${preview.keptLineCount} custom-rate ${preview.keptLineCount === 1 ? "Plan Line keeps its" : "Plan Lines keep their"} rate.`}
            </p>
            <p className="tabular-nums">
              Line revenue {dollars(preview.revenueBefore)} →{" "}
              <span className="font-semibold">{dollars(preview.revenueAfter)}</span>
            </p>
            {(preview.marginPercentBefore ?? null) !== null && (
              <p className="tabular-nums">
                Margin {preview.marginPercentBefore ?? "—"}% →{" "}
                <span className="font-semibold">{preview.marginPercentAfter ?? "—"}%</span>
              </p>
            )}
          </div>
        )}
      </div>

      {reprices || preview.otherDraftPlanCount > 0 ? (
        <p className="text-muted-foreground">
          {preview.otherDraftPlanCount > 0
            ? `${preview.otherDraftPlanCount} other Open ${preview.otherDraftPlanCount === 1 ? "plan" : "plans"} on ${card.clientAccountName} ${preview.otherDraftPlanCount === 1 ? "keeps its" : "keep their"} current rates.`
            : `No other Open plans on ${card.clientAccountName}.`}
        </p>
      ) : null}
    </div>
  );
}
